<template>
  <div>
    <v-btn
      v-if="isButton"
      outline
      round
      color="primary"
      class="grey--border"
      @click="getExcel"
      :loading="loading"
    >
      <Icon name="download" size="24px" class="mr-1" />
      <span>{{ buttonText ? buttonText : $t('common.Export') }}</span>
    </v-btn>

    <v-list-tile v-if="!isButton" @click="getExcel">
      <Icon name="download" size="24px" class="mr-2" />
      <v-list-tile-title>
        {{ buttonText ? buttonText : $t('common.Export') }}</v-list-tile-title
      >
    </v-list-tile>
  </div>
</template>

<script>
import download from 'downloadjs'
import excelHandling from '@/helpers/excel-handling'
import _ from 'lodash'

export default {
  props: {
    title: { default: '' },
    labelField: { default: 'label' },
    buttonText: { required: false },
    entries: { default: [] },
    components: { default: [] },
    isButton: { default: true }
  },
  data() {
    return {
      today: new Date(),
      loading: false
    }
  },
  computed: {
    fileTitle() {
      return `${this.title}${this.today.getFullYear()}-${
        this.today.getMonth() + 1
      }-${this.today.getDate()}`
    },
    multipleValuesComponentTypes() {
      return ['multi-select', 'custom-multi-select', 'hint-field-multiple']
    },
    multipleValuesComponent() {
      return ['loco-regional-anesthesia']
    },
    extendedExcelComponents() {
      return this.components.flatMap(c => {
        if (c.type === 'icd10') {
          return Array.from(Array(4).keys()).map(k => ({
            ...c,
            id: `${c.id}-${k + 1}`,
            text: `${c.text} ${k + 1}`,
            icdIndex: k,
            icdComponentId: c.id
          }))
        } else if (c.type === 'icd10-sec') {
          return Array.from(Array(8).keys()).map(k => ({
            ...c,
            id: `${c.id}-${k + 1}`,
            text: `${c.text} ${k + 1}`,
            icdIndex: k,
            icdComponentId: c.id
          }))
        } else if (
          this.multipleValuesComponentTypes.some(mvc => mvc === c.type) ||
          this.multipleValuesComponent.some(mvc => mvc === c.id)
        ) {
          const numberOfEntries = _.max(
            this.entries
              .filter(e => e[c.id] && e[c.id].length)
              .map(e =>
                Array.isArray(e[c.id])
                  ? e[c.id].length
                  : e[c.id].split(',').length
              )
          )

          return Array.from(Array(numberOfEntries).keys()).map(k => ({
            ...c,
            id: `${c.id}-${k + 1}`,
            text: `${c.text} ${k + 1}`,
            multiValueIndex: k,
            multiComponentId: c.id
          }))
        } else {
          return c
        }
      })
    },
    excelData() {
      return this.entries.map(log => {
        let mappedLog = {}

        this.extendedExcelComponents.forEach(component => {
          const key = component.id

          if (component.type === 'icd10') {
            const icdValues = log[component.icdComponentId]

            mappedLog[key] = this.getMultipleValue(
              component.icdIndex,
              icdValues
            )
          } else if (component.type === 'icd10-sec') {
            const icdValues = log[component.icdComponentId]

            mappedLog[key] = this.getMultipleValue(
              component.icdIndex,
              icdValues
            )
          } else if (
            this.multipleValuesComponentTypes.some(
              mvc => mvc === component.type
            ) ||
            this.multipleValuesComponent.some(
              mvc => mvc === component.multiComponentId
            )
          ) {
            const values = log[component.multiComponentId]

            mappedLog[key] = this.getMultipleValue(
              component.multiValueIndex,
              values
            )
          } else {
            mappedLog[key] = log[key]
          }
        })
        return this.normalizeLog(mappedLog)
      })
    },
    excelFields() {
      return this.extendedExcelComponents.reduce((result, c) => {
        result[c.id] = c[this.labelField]

        return result
      }, {})
    }
  },
  methods: {
    getMultipleValue(index, values) {
      if (values && (typeof values === 'string' || values instanceof String)) {
        const valuesArray = values.split(',')

        return valuesArray[index]
      } else {
        return ''
      }
    },
    normalizeLog(log) {
      const components = this.components

      components.forEach(c => {
        if (!log[c.id]) {
          log[c.id] = '' // Fix undefined
        }
      })

      return log
    },
    getExcel() {
      this.loading = true

      excelHandling.createExcel(this.excelFields, this.excelData).then(blob => {
        this.loading = false
        download(
          blob,
          `${this.fileTitle}.xlsx`,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        )
      })
    }
  }
}
</script>
